import { useState } from "react";
import { FileText, ImageDown, Printer, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api, errText } from "../lib/api";
import { Input } from "./ui/input";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FlyerPhone = ({ listing, phone, setPhone }) => {
  const { t } = useLanguage();
  const [value, setValue] = useState(phone);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/listings/${listing.id}/flyer-phone`, { phone: value });
      setPhone(data.flyer_phone); setValue(data.flyer_phone);
      toast.success(data.flyer_phone ? t("lff_saved") : t("lff_removed"));
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 pt-4 border-t border-slate-200/70" data-testid="lf-flyer-phone-box">
      <label className="text-sm font-semibold text-[#12234A] flex items-center gap-1.5"><Phone size={14} /> {t("lff_label")}</label>
      <p className="text-xs text-slate-500 mt-0.5">{t("lff_hint")}</p>
      <div className="flex gap-2 mt-2">
        <Input value={value} onChange={(e) => setValue(e.target.value)} maxLength={30} placeholder="(416) 555-0123" type="tel"
          className="h-10 rounded-xl bg-white max-w-[220px]" data-testid="lf-flyer-phone-input" />
        <button type="button" onClick={save} disabled={saving || value === phone} data-testid="lf-flyer-phone-save"
          className="h-10 px-4 rounded-xl bg-[#12234A] text-white text-sm font-semibold disabled:opacity-40 hover:bg-[#1c3166] transition-colors">
          {saving ? "…" : t("lff_save")}
        </button>
      </div>
    </div>
  );
};

export const FlyerDownloadButtons = ({ listing, lang }) => {
  const { t } = useLanguage();
  const href = (fmt) => `${BASE}/listings/${listing.id}/flyer.${fmt}?lang=${lang}&v=${encodeURIComponent(listing.flyer_phone || "")}`;
  return (
    <div className="flex flex-wrap justify-center gap-3" data-testid="gallery-flyer-buttons">
      <a href={href("pdf")} download data-testid="gallery-flyer-pdf-btn"
        className={`inline-flex items-center gap-2 h-11 px-5 rounded-xl ${listing.lf_type === "found" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} text-white text-sm font-semibold shadow-sm transition-colors`}>
        <FileText size={16} /> {t("lf_flyer_pdf")}
      </a>
      <a href={href("png")} download data-testid="gallery-flyer-png-btn"
        className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-slate-200 text-[#12234A] text-sm font-semibold hover:border-[#F5551E] transition-colors">
        <ImageDown size={16} /> {t("lf_flyer_png")}
      </a>
    </div>
  );
};

export const LostFlyerButtons = ({ listing }) => {
  const { t, lang: siteLang } = useLanguage();
  const { user } = useAuth();
  const [lang, setLang] = useState(siteLang === "fr" ? "fr" : "en");
  const [phone, setPhone] = useState(listing.flyer_phone || "");
  const lost = listing.lf_type === "lost";
  const isOwner = user?.user_id === listing.user_id;
  const href = (fmt) => `${BASE}/listings/${listing.id}/flyer.${fmt}?lang=${lang}&v=${encodeURIComponent(phone)}`;
  return (
    <div data-testid="lf-flyer-box" className={`mt-6 rounded-2xl border-2 p-5 ${lost ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
      <div className="flex items-start gap-3">
        <span className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center text-white ${lost ? "bg-red-600" : "bg-emerald-600"}`}>
          <Printer size={20} />
        </span>
        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-display font-bold text-[#12234A]">{t("lf_flyer_title")}</p>
            <div className="inline-flex rounded-lg bg-white border border-slate-200 p-0.5" data-testid="lf-flyer-lang">
              {["en", "fr"].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)} data-testid={`lf-flyer-lang-${l}`}
                  className={`px-3 h-7 rounded-md text-xs font-bold transition-colors ${lang === l ? "bg-[#12234A] text-white" : "text-slate-500 hover:text-[#12234A]"}`}>
                  {l === "en" ? "English" : "Français"}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-0.5">{t("lf_flyer_sub")}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a href={href("pdf")} download data-testid="lf-flyer-pdf-btn"
              className={`inline-flex items-center gap-2 h-10 px-4 rounded-xl text-white text-sm font-semibold transition-colors ${lost ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              <FileText size={16} /> {t("lf_flyer_pdf")}
            </a>
            <a href={href("png")} download data-testid="lf-flyer-png-btn"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-slate-200 text-[#12234A] text-sm font-semibold hover:border-[#F5551E] transition-colors">
              <ImageDown size={16} /> {t("lf_flyer_png")}
            </a>
          </div>
          {isOwner && <FlyerPhone listing={listing} phone={phone} setPhone={setPhone} />}
        </div>
      </div>
    </div>
  );
};
